<template>
  <v-container fluid class="statistics-page">
    <v-container fluid class="bg-white pb-6">
      <v-row>
        <v-col cols="12">
          <h2 class="headline font-weight-bold mb-2">Статистика</h2>
          <p class="subtitle-1 text--secondary">Статистические данные по проектам НПА и активности</p>
        </v-col>
      </v-row>

      <v-card class="mb-4" outlined>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="3">
              <v-select
                v-model="selectedType"
                :items="statTypes"
                label="Тип отчета"
                dense outlined
                @change="onTypeChange"
              ></v-select>
            </v-col>

            <v-col cols="12" md="3" v-if="needsFilter('year')">
              <v-select v-model="filters.year" :items="years" label="Год" dense outlined @change="loadData" clearable></v-select>
            </v-col>

            <v-col cols="12" md="3" v-if="needsFilter('range')">
              <v-select v-model="filters.startYear" :items="years" label="С года" dense outlined @change="loadData" clearable></v-select>
            </v-col>

            <v-col cols="12" md="3" v-if="needsFilter('range')">
              <v-select v-model="filters.endYear" :items="years" label="По год" dense outlined @change="loadData" clearable></v-select>
            </v-col>

            <v-col cols="12" md="3" v-if="needsFilter('developer')">
              <v-autocomplete
                v-model="filters.developerId"
                :items="developers"
                item-text="name"
                item-value="id"
                label="Разработчик"
                dense
                outlined
                clearable
                @change="onDeveloperChange"
              ></v-autocomplete>
            </v-col>

            <v-col cols="12" md="3" v-if="needsFilter('type')">
              <v-select v-model="filters.typeId" :items="projectTypes" item-text="name" item-value="id" label="Вид проекта" dense outlined @change="loadData" clearable></v-select>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-row class="mb-4">
        <v-col class="text-right">
          <v-btn color="success" class="mr-2" @click="exportData('excel')" :loading="loading" :disabled="loading">
            <v-icon left aria-hidden="true">mdi-file-excel</v-icon> Excel
          </v-btn>
           <v-btn color="error" @click="exportData('pdf')" :disabled="loading"><v-icon left aria-hidden="true">mdi-file-pdf</v-icon> PDF</v-btn>
        </v-col>
      </v-row>

      <v-card class="mb-4" v-if="chartConfig">
        <v-card-title class="primary white--text">{{ meta.title }}</v-card-title>
        <v-card-text>
          <div :style="{ height: `${getChartHeight(chartConfig)}px`, position: 'relative' }">
            <canvas
              ref="chartCanvas"
              role="img"
              :aria-label="getChartDescription(chartConfig, meta)"
            ></canvas>
          </div>
        </v-card-text>
      </v-card>

      <v-card v-if="tableData.items.length">
        <v-card-title>Таблица данных</v-card-title>
        <v-data-table
          :headers="tableData.headers"
          :items="tableData.items"
          dense
          :items-per-page="10"
          class="elevation-1"
        >
          <template v-slot:item="{ item }">
            <tr :class="{'font-weight-bold primary lighten-4': item.isTotal}">
              <td v-for="(header, i) in tableData.headers" :key="i">
                {{ item[header.value] }}
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card>

      <template v-if="developerChartConfig">
        <v-card ref="developerDetailsCard" class="mb-4 mt-6">
          <v-card-title class="primary white--text">{{ developerMeta.title }}</v-card-title>
          <v-card-text>
            <div :style="{ height: `${getChartHeight(developerChartConfig)}px`, position: 'relative' }">
              <canvas
                ref="developerChartCanvas"
                role="img"
                :aria-label="getChartDescription(developerChartConfig, developerMeta)"
              ></canvas>
            </div>
          </v-card-text>
        </v-card>

        <v-card v-if="developerTableData.items.length">
          <v-card-title>Таблица данных по разработчику</v-card-title>
          <v-data-table
            :headers="developerTableData.headers"
            :items="developerTableData.items"
            dense
            :items-per-page="10"
            class="elevation-1"
          >
            <template v-slot:item="{ item }">
              <tr :class="{'font-weight-bold primary lighten-4': item.isTotal}">
                <td v-for="(header, i) in developerTableData.headers" :key="i">
                  {{ item[header.value] }}
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </template>

      <v-snackbar v-model="showError" color="error" top>
        {{ errorMsg }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="showError = false">Закрыть</v-btn>
        </template>
      </v-snackbar>

      <v-overlay :value="loading"><v-progress-circular indeterminate size="64"></v-progress-circular></v-overlay>
    </v-container>
  </v-container>
</template>

<script>
import Chart from 'chart.js/auto';
import i18n from '../plugins/i18n';

export default {
  name: 'NpaStatistics',
  data: () => ({
    loading: false,
    currentYear: new Date().getFullYear(),
    selectedType: 'projects_by_years',
    statTypes: [
      { text: 'Проекты НПА по годам', value: 'projects_by_years' },
      { text: 'Проекты НПА по кварталам', value: 'projects_by_quarters' },
      { text: 'Проекты НПА по разработчикам', value: 'projects_by_developers' },
      { text: 'Проекты НПА по инициаторам', value: 'projects_by_initiators' },
      { text: 'Проекты НПА по видам', value: 'projects_by_types' },
      { text: 'Комментарии по годам', value: 'comments_by_years' },
      { text: 'Комментарии без ответов', value: 'unanswered_comments' },
      { text: 'Комментарии без ответов (по разработчикам)', value: 'unanswered_comments_by_developers' }
    ],
    filters: { year: null, startYear: null, endYear: null, developerId: null, typeId: null },
    years: [],
    developers: [],
    projectTypes: [],

    chartConfig: null,
    tableData: { headers: [], items: [] },
    meta: { title: '' },
    developerChartConfig: null,
    developerTableData: { headers: [], items: [] },
    developerMeta: { title: '' },

    chartInstance: null,
    developerChartInstance: null,
    errorMsg: '',
    showError: false,
    shouldScrollToDeveloperDetails: false
  }),
  mounted() {
    this.loadDicts();
    this.applyDefaultFiltersForType(this.selectedType);
    this.loadData();
  },
  beforeDestroy() {
    if (this.chartInstance) this.chartInstance.destroy();
    if (this.developerChartInstance) this.developerChartInstance.destroy();
  },
  methods: {
    needsFilter(filterName) {
      const map = {
        year: ['projects_by_quarters'],
        range: ['projects_by_years', 'projects_by_developers', 'projects_by_initiators', 'projects_by_types', 'comments_by_years', 'unanswered_comments', 'unanswered_comments_by_developers'],
        developer: ['projects_by_developers', 'projects_by_types', 'unanswered_comments', 'comments_by_years'],
        type: ['projects_by_years', 'projects_by_developers', 'projects_by_initiators', 'projects_by_types']
      };
      return map[filterName]?.includes(this.selectedType);
    },

    onTypeChange() {
      this.filters = { year: null, startYear: null, endYear: null, developerId: null, typeId: null };
      this.applyDefaultFiltersForType(this.selectedType);
      this.chartConfig = null;
      this.tableData = { headers: [], items: [] };
      this.resetDeveloperDetails();
      this.shouldScrollToDeveloperDetails = false;
      this.loadData();
    },

    onDeveloperChange() {
      this.shouldScrollToDeveloperDetails = this.selectedType === 'projects_by_developers' && !!this.filters.developerId;
      this.loadData();
    },

    applyDefaultFiltersForType(type) {
      if (this.needsFilterForType(type, 'year')) {
        this.filters.year = this.currentYear;
      }

      if (this.needsFilterForType(type, 'range')) {
        this.filters.startYear = this.currentYear;
        this.filters.endYear = this.currentYear;
      }
    },

    needsFilterForType(type, filterName) {
      const map = {
        year: ['projects_by_quarters'],
        range: ['projects_by_years', 'projects_by_developers', 'projects_by_initiators', 'projects_by_types', 'comments_by_years', 'unanswered_comments', 'unanswered_comments_by_developers'],
      };

      return map[filterName] && map[filterName].includes(type);
    },

    async loadData() {
      this.loading = true;
      try {
        if (this.selectedType === 'projects_by_developers') {
          const baseFilters = this.cleanFilters(false);
          const requests = [
            this.$http.get(`${i18n.locale}/api/statistics/${this.selectedType}`, {
              params: baseFilters
            })
          ];

          if (this.filters.developerId) {
            requests.push(
              this.$http.get(`${i18n.locale}/api/statistics/${this.selectedType}`, {
                params: this.cleanFilters()
              })
            );
          }

          const [topDataResponse, developerDataResponse] = await Promise.all(requests);
          const topData = topDataResponse.data;

          if (topData.success) {
            this.chartConfig = topData.chart;
            this.tableData = topData.table;
            this.meta = topData.meta;
          }

          if (developerDataResponse && developerDataResponse.data && developerDataResponse.data.success) {
            this.developerChartConfig = developerDataResponse.data.chart;
            this.developerTableData = developerDataResponse.data.table;
            this.developerMeta = developerDataResponse.data.meta;
          } else {
            this.resetDeveloperDetails();
          }

          this.$nextTick(() => {
            this.renderChart('chartCanvas', 'chartInstance', this.chartConfig);
            this.renderChart('developerChartCanvas', 'developerChartInstance', this.developerChartConfig);

            if (this.shouldScrollToDeveloperDetails && this.developerChartConfig) {
              this.scrollToDeveloperDetails();
            }
            this.shouldScrollToDeveloperDetails = false;
          });

          return;
        }

        this.resetDeveloperDetails();

        const { data } = await this.$http.get(`${i18n.locale}/api/statistics/${this.selectedType}`, {
          params: this.cleanFilters()
        });

        if (data.success) {
          this.chartConfig = data.chart;
          this.tableData = data.table;
          this.meta = data.meta;
          this.$nextTick(() => this.renderChart('chartCanvas', 'chartInstance', this.chartConfig));
        }
      } catch (e) {
        this.errorMsg = e.response?.data?.message || 'Ошибка загрузки данных';
        this.showError = true;
      } finally {
        this.loading = false;
      }
    },

    renderChart(refName, instanceName, chartConfig) {
      if (this[instanceName]) this[instanceName].destroy();
      const ctx = this.$refs[refName] && this.$refs[refName].getContext ? this.$refs[refName].getContext('2d') : null;
      if (!ctx || !chartConfig) return;

      this[instanceName] = new Chart(ctx, {
        type: chartConfig.type,
        data: chartConfig.data,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: true, position: 'top' },
            tooltip: { mode: 'index', intersect: false }
          },
          ...chartConfig.options
        }
      });
    },

    async exportData(format) {
      this.loading = true;
      try {
        const response = await this.$http.post(
          `${i18n.locale}/api/statistics/export/${format}`,
          { type: this.selectedType, filters: this.cleanFilters() },
          { responseType: 'blob' }
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = `statistics_${this.selectedType}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.errorMsg = 'Ошибка экспорта';
        this.showError = true;
      } finally {
        this.loading = false;
      }
    },

    getChartDescription(chartConfig, meta) {
      if (!chartConfig || !chartConfig.data) return meta.title || 'Статистика';

      const labels = chartConfig.data.labels || [];
      const datasets = chartConfig.data.datasets || [];
      const values = labels.map((label, index) => {
        const row = datasets.map((dataset) => `${dataset.label || meta.title}: ${dataset.data[index]}`).join(', ');
        return `${label}: ${row}`;
      });

      return `${meta.title || 'Статистика'}. ${values.join('; ')}`;
    },

    getChartHeight(chartConfig) {
      const labels = chartConfig && chartConfig.data && chartConfig.data.labels
        ? chartConfig.data.labels.length
        : 0;
      const isHorizontal = chartConfig
        && chartConfig.options
        && chartConfig.options.indexAxis === 'y';

      if (isHorizontal) {
        return Math.max(400, labels * 44);
      }

      return 400;
    },

    cleanFilters(includeDeveloper = true) {
      const res = {};
      for (const k in this.filters) {
        if (k === 'developerId' && !includeDeveloper) {
          continue;
        }

        if (this.filters[k]) {
          res[k] = this.filters[k];
        }
      }
      return res;
    },

    resetDeveloperDetails() {
      this.developerChartConfig = null;
      this.developerTableData = { headers: [], items: [] };
      this.developerMeta = { title: '' };

      if (this.developerChartInstance) {
        this.developerChartInstance.destroy();
        this.developerChartInstance = null;
      }
    },

    scrollToDeveloperDetails() {
      const card = this.$refs.developerDetailsCard;
      const element = card && card.$el ? card.$el : card;
      if (!element || !element.scrollIntoView) {
        return;
      }

      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    },

    async loadDicts() {
      try {
        const [y, d, t] = await Promise.all([
          this.$http.get(`${i18n.locale}/api/statistics/available-years`),
          this.$http.get(`${i18n.locale}/api/statistics/available-developers`),
          this.$http.get(`${i18n.locale}/api/statistics/available-project-types`)
        ]);
        this.years = y.data.years || [];
        this.developers = d.data.developers || [];
        this.projectTypes = t.data.types || [];
      } catch (e) { console.error('Ошибка загрузки справочников', e); }
    }
  }
};
</script>
